<template>
<div class="payment-result-container">
  <div class="row justify-content-center">
    <div class="col-md-8 col-lg-6">
      <div class="card payment-card error-card">
        
        <!-- Header Banner with Premium Styling and Pulse Warning Animation -->
        <div class="card-header-banner">
          <div class="error-icon-wrapper">
            <div class="error-icon-circle">
              <i class="fas fa-times"></i>
            </div>
            <div class="pulse-ring"></div>
          </div>
          <h2 class="title-primary">{{ $t("message.payment_failed") }}</h2>
          <p class="subtitle-primary">{{ $t("message.payment_failed_msg") }}</p>
        </div>

        <div class="card-body p-4">
          <!-- Transaction context -->
          <div class="invoice-summary-details" v-if="transactionId">
            <h5 class="section-title">
              <i class="fas fa-exclamation-circle me-2"></i>Reference Information
            </h5>
            
            <div class="details-grid">
              <div class="detail-row">
                <span class="detail-label">{{ $t("message.transaction_id") }}</span>
                <span class="detail-value text-highlight">{{ transactionId }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Status</span>
                <span class="detail-value">
                  <span class="status-badge error-badge">
                    <i class="fas fa-ban me-1"></i>Unpaid
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Helpful Instructions -->
          <div class="help-section mt-4">
            <h6 class="help-title">What can you do now?</h6>
            <ul class="help-list">
              <li>
                <i class="fas fa-check-circle text-success me-2"></i>
                Verify that your account has sufficient balance and try checking out again.
              </li>
              <li>
                <i class="fas fa-check-circle text-success me-2"></i>
                Ensure your internet connection is stable during authentication.
              </li>
              <li>
                <i class="fas fa-check-circle text-success me-2"></i>
                You can try using a different payment option (KHQR, Credit/Debit card).
              </li>
            </ul>
          </div>

          <!-- Support Details -->
          <div class="support-card mt-4 p-3 bg-light rounded border">
            <div class="d-flex align-items-start">
              <i class="fas fa-headset fa-2x text-muted me-3 mt-1"></i>
              <div>
                <h6 class="mb-1 text-dark fw-bold">Need Assistance?</h6>
                <p class="mb-0 text-muted small">
                  {{ $t("message.serc_phone") }}<br>
                  Email us at support or contact the finance department.
                </p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="action-buttons-group mt-4">
            <router-link :to="{ name: 'shoppingCard' }" class="btn btn-retry-payment">
              <i class="fas fa-redo me-2"></i>{{ $t("message.retry_payment") }}
            </router-link>
            
            <router-link :to="{ name: 'Dashboard' }" class="btn btn-to-dashboard">
              <i class="fas fa-home me-2"></i>{{ $t("message.back_to_dashboard") }}
            </router-link>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>
</template>

<script>
export default {
  name: 'PaymentFailed',
  data() {
    return {
      transactionId: null
    };
  },
  created() {
    // Get transaction ID from route parameters or query parameters
    this.transactionId = this.$route.params.transactionId || this.$route.query.transaction_id || this.$route.query.transactionID;
  }
};
</script>

<style scoped>
.payment-result-container {
  padding: 60px 15px;
  background-color: #f8f9fa;
  min-height: 80vh;
}

.payment-card {
  border: none;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background: #ffffff;
  transition: all 0.3s ease;
}

/* Error Header Stylings */
.error-card .card-header-banner {
  background: linear-gradient(135deg, #c0392b 0%, #e74c3c 100%);
  padding: 40px 20px;
  text-align: center;
  color: #ffffff;
  position: relative;
}

.title-primary {
  font-size: 1.8rem;
  font-weight: 800;
  margin-top: 15px;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.subtitle-primary {
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

/* Animated Pulse Cross Icon */
.error-icon-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon-circle {
  width: 80px;
  height: 80px;
  background-color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e74c3c;
  font-size: 32px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  z-index: 2;
  animation: shake 0.5s ease-in-out;
}

.pulse-ring {
  border: 3px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  border-radius: 50%;
  height: 90px;
  width: 90px;
  position: absolute;
  z-index: 1;
  animation: pulsate 1.8s ease-out infinite;
  opacity: 0;
}

@keyframes pulsate {
  0% {
    transform: scale(0.8, 0.8);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1.2, 1.2);
    opacity: 0;
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-6px); }
  40%, 80% { transform: translateX(6px); }
}

/* Section Title */
.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2b2b2b;
  margin-bottom: 16px;
  border-bottom: 2px solid #f1f3f5;
  padding-bottom: 8px;
}

.section-title i {
  color: #e74c3c;
}

/* Details Grid */
.details-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #f8f9fa;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
}

.detail-label {
  color: #6c757d;
  font-weight: 500;
}

.detail-value {
  font-weight: 600;
  color: #2b2b2b;
}

.text-highlight {
  color: #c0392b;
  font-weight: 700;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.error-badge {
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
}

/* Help Section */
.help-section {
  padding: 5px;
}

.help-title {
  font-weight: 700;
  color: #2b2b2b;
  margin-bottom: 12px;
}

.help-list {
  list-style: none;
  padding-left: 0;
}

.help-list li {
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: #495057;
  display: flex;
  align-items: center;
}

.help-list li i {
  flex-shrink: 0;
  color: #e74c3c !important;
}

/* Action Buttons Group */
.action-buttons-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-retry-payment {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  color: #ffffff;
  border: none;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(192, 57, 43, 0.2);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-retry-payment:hover {
  background: linear-gradient(135deg, #c0392b 0%, #962d22 100%);
  transform: translateY(-1px);
  color: #ffffff;
}

.btn-to-dashboard {
  background-color: #ffffff;
  border: 2px solid #e9ecef;
  color: #495057;
  font-weight: 700;
  padding: 12px 20px;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-to-dashboard:hover {
  background-color: #f8f9fa;
  border-color: #ced4da;
  color: #212529;
}

@media (min-width: 576px) {
  .action-buttons-group {
    flex-direction: row;
  }
  .btn-retry-payment {
    flex: 1;
  }
  .btn-to-dashboard {
    flex: 1;
  }
}
</style>
